import React, { Fragment, useEffect, useMemo, useState } from "react";
import { ProfileSetupButtons } from "../ProfileSetupButtons";
import { BIO_KEY, ONDATO_APPROVAL, VERIFY_FACE_ID_KEY } from "../../../../enums/profileSetupEnums";
import { Box, Button, CircularProgress, Divider, Grid2 as Grid, Stack, Typography } from "@mui/material";
import { DesktopDatePickerField, FormAutoComplete, FormTextField } from "../../../generic/GenericTextFields";
import { COUNTRY_WITH_CODES } from "../../../../enums/countries";
import { useFormik } from "formik";
import { number, object, string } from "yup";
import { birthdayValidation, countryValidation, emailValidation } from "../../../../helpers/genericFormValidations";
import { getOndatoLink } from "../../../../config/apisauce";
import { QRCodeCanvas } from "qrcode.react";
import { useTheme } from "@emotion/react";
import useNotiStack from "../../../../hooks/useNotiStack";

export const VerifyFaceAndIdForm = ({ handleChangeForm, initialUserDetail, handleSubmit }) => {
  const { success } = useNotiStack();
  const ondatoDetails = initialUserDetail?.user?.ondato_details?.meta ?? null;
  const theme = useTheme();
  const [qrCode, setQrCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const countryValue = useMemo(() => {
    return COUNTRY_WITH_CODES?.find(item => item?.name === initialUserDetail?.country);
  });

  const formik = useFormik({
    initialValues: {
      email: initialUserDetail?.user?.email ?? '',
      birthday: initialUserDetail?.birthday ?? '',
      country: countryValue ?? null
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: object().shape({
      ...emailValidation,
      ...birthdayValidation,
      ...countryValidation,
      'first_name': string().trim().required('First name is required'),
      'last_name': string().trim().required('Last name is required'),
      'mobile_number': number().min(0, 'Mobile number cannot be negative')
    }),
    onSubmit: (values) => handleGenerateQR(values),
  });

  const handleGenerateQR = async (values) => {
    setIsLoading(true);
    const params = {
      user_id: initialUserDetail?.user_id,
      dateOfBirth: values?.birthday,
      email: values?.email,
      firstName: values?.first_name,
      lastName: values?.last_name,
      middleName: values?.middle_name,
      phoneNumber: values?.mobile_number,
      countryCode: values?.country?.isoCode
    };

    const { ok, data } = await getOndatoLink(params);
    if (ok) {
      setQrCode(data?.link);
    }
    setIsLoading(false);
  };

  const fetchExistingOndato = async () => {
    const { ok, data } = await getOndatoLink({ ...ondatoDetails, user_id: initialUserDetail?.user_id });
    if (ok) {
      setQrCode(data?.link);
    }
  };

  const handleRegenerateLink = async () => {
    setIsLoading(true);
    const params = {
      dateOfBirth: formik?.values?.birthday,
      email: formik?.values?.email,
      firstName: formik?.values?.first_name,
      lastName: formik?.values?.last_name,
      middleName: formik?.values?.middle_name,
      phoneNumber: formik?.values?.mobile_number,
      countryCode: formik?.values?.country?.isoCode
    };
    const details = ondatoDetails ?? params;
    const { ok, data } = await getOndatoLink({ ...details, user_id: initialUserDetail?.user_id, is_reverify: true });
    if (ok) {
      setQrCode(data?.link);
      success('New verification link generated!');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (ondatoDetails) fetchExistingOndato();
  }, []);

  return (
    <Fragment>
      {
        qrCode ? <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
          <Typography color="text.semiLight" mb={2} textAlign={{ xxs: 'left', sm: 'center' }}>
            Scan the QR code below with your mobile device to start the verification process. Follow the prompts on your phone to upload a clear picture of your ID and take a live photo of your face.
          </Typography>
          {
            isLoading ? <Fragment>
              <Box sx={{ height: '42.7vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Stack alignItems={'center'} gap={2}>
                  <CircularProgress />
                  <Typography>Generating new verification link. Please wait...</Typography>
                </Stack>
              </Box>
            </Fragment> : <Fragment>
              <Typography
                display={{ xxs: "none", xsm: "inline" }}
                variant="subtitle1"
                fontWeight={700}
                textAlign={"center"}
                mb={1}
              >
                Scan QR code with your mobile camera or QR reader app
              </Typography>
              <Typography
                display={{ xxs: "inline", xsm: "none" }}
                variant="subtitle1"
                fontWeight={700}
                textAlign={"center"}
                mb={1}
              >
                Tap the Verify button to begin
              </Typography>
              <QRCodeCanvas
                value={qrCode}
                fgColor={theme.palette.primary.main}
                size={250}
              />

              <Box width={250} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Divider component="div" role="presentation" style={{ width: '100%' }}>
                  <Typography fontSize={12} my={.5} color="textSecondary"> or </Typography>
                </Divider>
              </Box>

              <Button
                component="a"
                href={qrCode}
                target="_blank"
                variant="contained"
                sx={{ m: 0, width: 250, textTransform: 'none' }}
                color="primary"
                rel="noopener noreferrer"
              >
                Click here to verify
              </Button>
              <Typography textAlign="center" mt={2} mb={1} color="text.semiLight">
                QR not working?&nbsp;
                <Typography
                  component="span"
                  role="button"
                  tabIndex={0}
                  onClick={handleRegenerateLink}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 700,
                    color: theme?.palette?.primary?.main,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Generate new link
                </Typography>
              </Typography>

              <Typography color="text.semiLight" margin={"12px"} textAlign={"center"}>
                Once you&apos;ve completed the verification, click &quot;I&apos;ve Verified&quot; to proceed.
              </Typography>
            </Fragment>
          }
        </Box> : <Grid container spacing={1.5}>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <FormTextField
              value={formik?.values.first_name}
              placeholder={'John'}
              label={'First Name'}
              error={Boolean(formik?.errors?.first_name)}
              helperText={formik?.errors?.first_name}
              handleChange={(value) => formik.setFieldValue('first_name', value)}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <FormTextField
              value={formik?.values.last_name}
              placeholder={'Doe'}
              label={'Last Name'}
              error={Boolean(formik?.errors?.last_name)}
              helperText={formik?.errors?.last_name}
              handleChange={(value) => formik.setFieldValue('last_name', value)}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <FormTextField
              isRequired={false}
              value={formik?.values.middle_name}
              placeholder={'Smith'}
              label={'Middle Name'}
              handleChange={(value) => formik.setFieldValue('middle_name', value)}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <FormTextField
              value={formik?.values.email}
              placeholder={'example@email.com'}
              label={'Email'}
              error={Boolean(formik?.errors?.email)}
              helperText={formik?.errors?.email}
              handleChange={(value) => formik.setFieldValue('email', value)}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <FormTextField
              isRequired={false}
              type={'number'}
              placeholder={'+12345678900'}
              value={formik?.values?.mobile_number}
              label={'Mobile Number'}
              handleChange={(value) => formik.setFieldValue('mobile_number', value)}
              error={Boolean(formik?.errors?.mobile_number)}
              helperText={formik?.errors?.mobile_number}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <DesktopDatePickerField
              value={formik?.values?.birthday}
              label={'Birthday'}
              error={Boolean(formik?.errors?.birthday)}
              helperText={formik?.errors?.birthday}
              handleChange={(value) => formik.setFieldValue('birthday', value)}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <FormAutoComplete
              value={formik?.values.country}
              getOptionLabel={(option) => option.name}
              options={COUNTRY_WITH_CODES}
              label={'Country'}
              placeholder={'Please Select'}
              error={Boolean(formik?.errors?.country)}
              helperText={formik?.errors?.country}
              handleChange={(value) => formik.setFieldValue('country', value)}
            />
          </Grid>
        </Grid>
      }
      <Box p={1}>
        <ProfileSetupButtons
          handleBack={() => handleChangeForm(BIO_KEY)}
          currentForm={VERIFY_FACE_ID_KEY}
          handleSubmit={() => qrCode ? handleSubmit({}, ONDATO_APPROVAL) : formik.handleSubmit()}
          submitText={qrCode ? "I've Verified" : 'Next'}
          loading={isLoading}
        />
      </Box>
    </Fragment>
  );
};